'use client';

import React, { useEffect, useRef } from 'react';
import { Slide } from '@/shared/types';
import { PresentationMarkdown } from './presentationMarkdown';

// Interfaces and types
interface DescriptionPaneProps {
  slide: Slide;
  presentationHeight?: number;
}

// Component definition
const DescriptionPane: React.FC<DescriptionPaneProps> = ({ slide, presentationHeight }) => {
  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (descriptionRef.current) {
      descriptionRef.current.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      descriptionRef.current.querySelectorAll('p').forEach((p) => {
        p.style.wordBreak = 'break-word';
      });
    }
  }, [slide]);

  return (
    <div
      ref={descriptionRef}
      data-testid="description-pane"
      className="slide-boxes box-border mini sm:lg flex flex-col items-start justify-start w-full min-w-0 max-w-full min-h-0 break-words [overflow-wrap:anywhere] sm:overflow-y-auto sm:overflow-x-hidden custom-scrollbar2"
      style={{
        // slide-boxes is a 20% ink wash. theme-core is not global, so the
        // inverse surface needs a fallback or this panel stays transparent
        // over the video (TR-3842).
        backgroundColor: 'var(--surface-inverse, #0f1d32)',
        ...(typeof presentationHeight === 'number'
          ? { height: presentationHeight, maxHeight: presentationHeight, flex: 'none' }
          : {}),
      }}
    >
      {/*
        The pane is `items-start`, so without an explicit width this block is
        sized to fit its content. A cited spreadsheet chunk is far wider than
        the pane, so it would stretch to the table's max-content width, leaving
        the markdown table's own `overflow-x-auto` container with nothing to
        overflow — the table then runs under the pane's `overflow-x-hidden`
        edge with no way to scroll to the remaining columns (TR-2386).
      */}
      <PresentationMarkdown className="w-full min-w-0 max-w-full">{slide.slideDescription}</PresentationMarkdown>
    </div>
  );
}

export default DescriptionPane;
